import React, {useEffect} from 'react';
import {Box, Input} from "@chakra-ui/react";

interface LanguageInputProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  languages: string[];
  selectedLanguage: string;
  onSelectLanguage: (lang: string) => void;
  onChange: (value: string) => void;
  placeholder: string;
  label?: string;
}

const LanguageInput: React.FC<LanguageInputProps> = (
  {
    isOpen,
    setIsOpen,
    languages,
    selectedLanguage,
    onSelectLanguage,
    onChange,
    placeholder,
    label
  }: LanguageInputProps) => {
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);

  const filteredLanguages: string[] = languages.filter((lang: string) =>
    lang.toLowerCase().includes(selectedLanguage.toLowerCase())
  );

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({block: "nearest"});
    }
  }, [highlightedIndex]);


  return (
    <Box position="relative" w="100%">
      {label && (
        <div className="flex mb-2 base2 font-semibold">
          {label}
        </div>)
      }
      <Input
        h={"50px"}
        p={2}
        autoFocus={false}
        placeholder={placeholder}
        value={selectedLanguage}
        onChange={(e) => {
          if (!isOpen) {
            setIsOpen(true)
          }
          onChange(e.target.value);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        onClick={() => setIsOpen(true)}
        onBlur={() => {
          // Delay to allow click on dropdown items
          setTimeout(() => setIsOpen(false), 100);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
              Math.min(prev + 1, filteredLanguages.length - 1)
            );
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          } else if (e.key === "Enter" && highlightedIndex >= 0) {
            e.preventDefault();
            onSelectLanguage(filteredLanguages[highlightedIndex]);
            setIsOpen(false);
          }
        }}
        color="white"
        border="1px solid gray"
        _focus={{borderColor: "brandPurple.500"}}
        sx={{
          border: "none",
          outline: "none",
          boxShadow: "0 0 0 0.5px gray",
          color: "white",
          _focus: {
            boxShadow: "0 0 0 1px brandPurple.500",
          },
          _hover: {
            boxShadow: "0 0 0 1px brandPurple.500",
          },
        }}
      />
      {isOpen && filteredLanguages.length > 0 && (
        <Box
          position="absolute"
          zIndex="10"
          bg="black"
          mt={1}
          w="100%"
          maxH="200px"
          overflowY="auto"
          border="1px solid gray"
          borderRadius="md"
        >
          {filteredLanguages.map((lang: string, index: number) => (
            <Box
              key={lang}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              px={4}
              py={2}
              _hover={{bg: "brandPurple.500", cursor: "pointer"}}
              bg={highlightedIndex === index ? "brandPurple.500" : ""}
              color="white"
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseDown={() => {
                onSelectLanguage(lang);
                setIsOpen(false);
              }}
            >
              {lang}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default LanguageInput;